import type { Topic, GapSignal, StudentUnderstandingProfile, QuizQuestion } from "./types";
import { interpretStudentReasoning, buildGapSignalsFromInterpretation } from "./interpretation-engine";
import {
  initializeStudentMasteryState,
  updateBKTMasteryFromGapSignal,
  updateBKTMastery,
  calculateMasteryPercent,
  getMasteryLevel,
  type StudentMasteryState,
  type BKTParams
} from "./bkt-engine";

export type RootCauseItem = {
  topicId: string;
  topicName: string;
  impactScore: number;
  depth: number;
  affectedTopics: string[];
};

export type PropagationPathNode = {
  topicId: string;
  isRootCause: boolean;
  mastery: number;
};

export type RootCauseResult = {
  rootCauses: RootCauseItem[];
  propagationPath: PropagationPathNode[];
  confidence: number;
  explanation: string;
};

export type PropagationTrace = {
  path: string[];
  score: number;
};

export type WeakLink = {
  topicId: string;
  topicName: string;
  downstreamCount: number;
  impact: number;
};

export type RecommendedPath = {
  steps: Array<{ topicId: string; name: string; reason: string }>;
  totalSteps: number;
  estimatedDuration: string;
  rootCauseCount: number;
  gapCount: number;
};

let API_BASE = ''; // set via setApiBase() at startup
import {
  calibrateQuestion,
  selectNextQuestion,
  createMicroAssessment,
  updateAssessmentEstimate,
  convertMasteryToTheta,
  type CalibratedQuestion,
  type MicroAssessment
} from "./irt-engine";
import {
  generateSocraticDialogue,
  analyzeStudentResponse,
  selectNextDialogueTopic,
  buildSocraticPromptForKnowledgeCore,
  shouldEscalateToDirectInstruction,
  type SocraticExchange
} from "./socratic-engine";

export type IntelligenceState = {
  masteryState: StudentMasteryState;
  gapSignals: GapSignal[];
  socraticHistory: SocraticExchange[];
  assessments: Map<string, MicroAssessment>;
  calibratedQuestions: Map<string, CalibratedQuestion>;
  lastUpdated: string;
};

export type ProcessResult = {
  interpretation?: ReturnType<typeof interpretStudentReasoning>;
  gapSignals: GapSignal[];
  rootCauses: RootCauseResult | null;
  socraticPrompt?: string;
  interventionTriggered: boolean;
  updatedMastery: StudentMasteryState;
};

export function setApiBase(url: string) {
  API_BASE = url;
}

async function fetchJson(path: string, body: unknown): Promise<any> {
  const base = API_BASE || '';
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API ${path}: ${res.status}`);
  return res.json();
}

export function initializeIntelligence(
  studentId: string,
  topics: Topic[]
): IntelligenceState {
  const topicIds = topics.map(t => t.id);
  
  return {
    masteryState: initializeStudentMasteryState(studentId, topicIds),
    gapSignals: [],
    socraticHistory: [],
    assessments: new Map(),
    calibratedQuestions: new Map(),
    lastUpdated: new Date().toISOString()
  };
}

export async function processStudentInput(
  state: IntelligenceState,
  studentMessage: string,
  topics: Topic[],
  currentTopicId?: string,
): Promise<ProcessResult> {
  const currentTopic = topics.find(t => t.id === currentTopicId);
  
  const interpretation = interpretStudentReasoning(
    studentMessage,
    { topicId: currentTopicId },
    topics,
    []
  );
  
  const gapSignals = buildGapSignalsFromInterpretation(interpretation, state.masteryState.studentId);
  
  let updatedMastery = { ...state.masteryState };
  
  for (const signal of gapSignals) {
    const topicMastery = updatedMastery.topicMastery[signal.topicId];
    if (topicMastery) {
      const update = updateBKTMasteryFromGapSignal(
        topicMastery,
        signal.severity,
        signal.confidence || 0.7
      );
      updatedMastery.topicMastery[signal.topicId] = update.updatedParams;
    }
  }
  
  // Fetch root cause analysis from the server (Cypher-backed)
  let rootCauses: RootCauseResult | null = null;
  if (gapSignals.length > 0 && currentTopic && API_BASE) {
    try {
      const observed = gapSignals.map(s => ({ concept: s.topicId, weight: s.confidence ?? 1.0 }));
      if (observed.length === 1) {
        const data = await fetchJson('/api/infer/root-causes', {
          studentId: state.masteryState.studentId,
          observed,
        });
        rootCauses = mapRootCausesResponse(data.rootCauses, observed);
      } else {
        const data = await fetchJson('/api/infer/common-root-causes', {
          studentId: state.masteryState.studentId,
          observed,
        });
        rootCauses = mapCommonRootCausesResponse(data.commonRootCauses, observed);
      }
    } catch {
      // Server unavailable — skip root cause analysis
    }
  }
  
  let socraticPrompt: string | undefined;
  let interventionTriggered = false;
  
  if (interpretation.confidence > 0.6 && interpretation.classifiedError !== "no_error") {
    if (currentTopic) {
      const exchange = generateSocraticDialogue(
        gapSignals[0],
        currentTopic,
        state.socraticHistory
      );
      socraticPrompt = exchange.socraticQuestion;
      interventionTriggered = true;
    }
  }
  
  return {
    interpretation,
    gapSignals,
    rootCauses,
    socraticPrompt,
    interventionTriggered,
    updatedMastery
  };
}

function mapRootCausesResponse(records: any[], observed: Array<{ concept: string }>): RootCauseResult {
  const items: RootCauseItem[] = (records || []).map((r: any) => ({
    topicId: r.topicId || r.concept,
    topicName: r.topicName || r.conceptName,
    impactScore: 1 - (r.score ?? 0),
    depth: r.depth ?? 0,
    affectedTopics: observed.map(o => o.concept),
  }));
  return {
    rootCauses: items,
    propagationPath: items.map(r => ({
      topicId: r.topicId,
      isRootCause: r.depth === 0,
      mastery: 1 - r.impactScore,
    })),
    confidence: items[0]?.impactScore ?? 1,
    explanation: items.length > 0
      ? `Found ${items.length} root cause(s) via Cypher backward traversal.`
      : 'No root causes identified.',
  };
}

function mapCommonRootCausesResponse(records: any[], observed: Array<{ concept: string }>): RootCauseResult {
  const items: RootCauseItem[] = (records || []).map((r: any) => ({
    topicId: r.topicId || r.concept,
    topicName: r.topicName || r.conceptName,
    impactScore: 1 - (r.score ?? 0),
    depth: r.depth ?? 0,
    affectedTopics: r.sharedWith ?? observed.map(o => o.concept),
  }));
  return {
    rootCauses: items,
    propagationPath: items.map(r => ({
      topicId: r.topicId,
      isRootCause: r.depth === 0,
      mastery: 1 - r.impactScore,
    })),
    confidence: items[0]?.impactScore ?? 1,
    explanation: items.length > 0
      ? `Found ${items.length} shared root cause(s) via Cypher backward traversal.`
      : 'No common root causes identified.',
  };
}

export function processQuizAnswer(
  state: IntelligenceState,
  question: QuizQuestion,
  correct: boolean,
  topics: Topic[]
): {
  updatedState: IntelligenceState;
  updatedMastery: StudentMasteryState;
  gapSignals: GapSignal[];
  assessmentComplete: boolean;
} {
  const questionId = question.id;
  let updatedState = { ...state };
  let updatedMastery = { ...state.masteryState };
  let gapSignals: GapSignal[] = [];
  
  const calibratedQuestion = updatedState.calibratedQuestions.get(questionId);
  if (calibratedQuestion) {
    const assessment = updatedState.assessments.get(calibratedQuestion.calibratedForTopicId);
    
    if (assessment) {
      const { updatedAssessment, isComplete } = updateAssessmentEstimate(
        assessment,
        [{ questionId, correct }]
      );
      
      updatedState.assessments.set(calibratedQuestion.calibratedForTopicId, updatedAssessment);
      
      if (correct) {
        const topicMastery = updatedMastery.topicMastery[calibratedQuestion.calibratedForTopicId];
        if (topicMastery) {
          const update = updateBKTMasteryFromGapSignal(
            topicMastery,
            "low",
            0.9
          );
          updatedMastery.topicMastery[calibratedQuestion.calibratedForTopicId] = update.updatedParams;
        }
      } else {
        const topicMastery = updatedMastery.topicMastery[calibratedQuestion.calibratedForTopicId];
        if (topicMastery) {
          const update = updateBKTMasteryFromGapSignal(
            topicMastery,
            "medium",
            0.8
          );
          updatedMastery.topicMastery[calibratedQuestion.calibratedForTopicId] = update.updatedParams;
        }
        
        gapSignals.push({
          id: `quiz-gap-${questionId}-${Date.now()}`,
          topicId: calibratedQuestion.calibratedForTopicId,
          topicName: topics.find(t => t.id === calibratedQuestion.calibratedForTopicId)?.name || "Unknown",
          signalSource: "quiz",
          severity: correct ? "low" : "medium",
          confidence: 0.8,
          evidence: correct 
            ? "Student answered correctly on first attempt"
            : "Student required quiz question to identify gap",
          recommendedAction: correct 
            ? "Continue to next topic"
            : "Review prerequisite concepts"
        });
      }
      
      return {
        updatedState,
        updatedMastery,
        gapSignals,
        assessmentComplete: isComplete
      };
    }
  }

  const resolvedTopicId =
    question.topicId || question.prerequisiteTopicIds[0] || topics.find((t) => t.name === question.conceptTag)?.id;

  if (resolvedTopicId) {
    const topicMastery = updatedMastery.topicMastery[resolvedTopicId];
    if (topicMastery) {
      const update = updateBKTMastery(topicMastery, correct);
      updatedMastery.topicMastery[resolvedTopicId] = update.updatedParams;

      if (!correct) {
        gapSignals.push({
          id: `quiz-gap-${questionId}-${Date.now()}`,
          topicId: resolvedTopicId,
          topicName: topics.find((t) => t.id === resolvedTopicId)?.name || "Unknown",
          signalSource: "quiz",
          severity: "medium",
          confidence: 0.75,
          evidence: "Incorrect answer on subject-scoped quiz item.",
          recommendedAction: "Review approved materials for this topic, then retry."
        });
      }
    }
  }

  return {
    updatedState,
    updatedMastery,
    gapSignals,
    assessmentComplete: false
  };
}

export function getTopicMastery(
  state: IntelligenceState,
  topicId: string
): { percent: number; level: string; params: BKTParams } | null {
  const params = state.masteryState.topicMastery[topicId];
  if (!params) return null;
  
  const percent = calculateMasteryPercent(params.pKnown);
  const level = getMasteryLevel(percent);
  
  return { percent, level, params };
}

export function getMasteryVisualization(
  intelligence: IntelligenceState,
  topics: Topic[]
): Array<{
  topicId: string;
  topicName: string;
  percent: number;
  level: string;
  color: string;
  prerequisites: string[];
  assessed: boolean;
}> {
  return topics.map(topic => {
    const mastery = getTopicMastery(intelligence, topic.id);
    const params = mastery?.params;
    const assessed = (params?.attempts || 0) > 0;

    let percent = mastery?.percent || 0;
    let level = mastery?.level || "needs_work";

    if (!assessed) {
      percent = 0;
      level = "no_data";
    }
    
    let color = "#d9d9d9";
    if (assessed) {
      if (level === "needs_work") color = "#f5222d";
      else if (level === "developing") color = "#faad14";
      else if (level === "competent") color = "#1890ff";
      else if (level === "proficient") color = "#52c41a";
    }
    
    return {
      topicId: topic.id,
      topicName: topic.name,
      percent,
      level,
      color,
      prerequisites: topic.prerequisiteTopicIds || [],
      assessed
    };
  });
}

export function getRootCauseAnalysis(
  intelligence: IntelligenceState,
  topics: Topic[]
): {
  failedTopics: string[];
  rootCauses: RootCauseResult;
  learningPath: string[];
  pattern: "root_only" | "surface_only" | "chain" | "multi_root" | "unknown";
  recommendation: string;
  propagationTraces: PropagationTrace[];
  weakLinks: WeakLink[];
} {
  const failedTopics = Object.entries(intelligence.masteryState.topicMastery)
    .filter(([_, params]) => params.pKnown < 0.5)
    .map(([id]) => id);

  if (failedTopics.length === 0) {
    return {
      failedTopics: [],
      rootCauses: {
        rootCauses: [],
        propagationPath: [],
        confidence: 1,
        explanation: "No gaps detected. Keep up the great work!"
      },
      learningPath: [],
      pattern: "unknown",
      recommendation: "Continue with current topics. Consider exploring advanced concepts.",
      propagationTraces: [],
      weakLinks: [],
    };
  }

  return {
    failedTopics,
    rootCauses: {
      rootCauses: [],
      propagationPath: [],
      confidence: 1,
      explanation: `${failedTopics.length} gap(s) detected. Use fetchRootCauseAnalysis() for Cypher-backed results.`,
    },
    learningPath: [],
    pattern: "unknown",
    recommendation: "Call fetchRootCauseAnalysis() for Cypher-backed graph analysis.",
    propagationTraces: [],
    weakLinks: [],
  };
}

export async function fetchRootCauseAnalysis(
  intelligence: IntelligenceState,
  topics: Topic[],
): Promise<{
  failedTopics: string[];
  rootCauses: RootCauseResult;
  learningPath: string[];
  pattern: "root_only" | "surface_only" | "chain" | "multi_root" | "unknown";
  recommendation: string;
  propagationTraces: PropagationTrace[];
  weakLinks: WeakLink[];
}> {
  const failedTopics = Object.entries(intelligence.masteryState.topicMastery)
    .filter(([_, params]) => params.pKnown < 0.5)
    .map(([id]) => id);

  if (failedTopics.length === 0 || !API_BASE) {
    return getRootCauseAnalysis(intelligence, topics);
  }

  const observed = failedTopics.map(id => ({ concept: id, weight: 1.0 }));

  try {
    const [rootData, traceData, weakData] = await Promise.all([
      observed.length === 1
        ? fetchJson('/api/infer/root-causes', { studentId: intelligence.masteryState.studentId, observed })
        : fetchJson('/api/infer/common-root-causes', { studentId: intelligence.masteryState.studentId, observed }),
      fetchJson('/api/infer/trace', { studentId: intelligence.masteryState.studentId, observed }),
      fetchJson('/api/infer/weak-links', { studentId: intelligence.masteryState.studentId }),
    ]);

    const rootCauses = observed.length === 1
      ? mapRootCausesResponse(rootData.rootCauses, observed)
      : mapCommonRootCausesResponse(rootData.commonRootCauses ?? rootData.rootCauses, observed);

    const propagationTraces: PropagationTrace[] = (traceData.traces || []).map((t: any) => ({
      path: (t.path || []).map((p: any) => p.concept),
      score: t.totalScore ?? 0,
    }));

    const weakLinks: WeakLink[] = (weakData.weakLinks || []).map((w: any) => ({
      topicId: w.topicId || w.concept,
      topicName: w.topicName || w.conceptName,
      downstreamCount: w.downstreamCount ?? 0,
      impact: w.weaknessScore ?? 0,
    }));

    const pattern = rootCauses.rootCauses.length === 1 && rootCauses.rootCauses[0].depth === 0
      ? 'root_only'
      : rootCauses.rootCauses.length > 1
      ? 'multi_root'
      : 'chain';

    return {
      failedTopics,
      rootCauses,
      learningPath: rootCauses.rootCauses.map(r => r.topicId),
      pattern,
      recommendation: rootCauses.rootCauses.length > 0
        ? `Found ${rootCauses.rootCauses.length} root cause(s). Prioritize the weakest foundational concepts.`
        : 'No clear root causes identified.',
      propagationTraces,
      weakLinks,
    };
  } catch {
    return getRootCauseAnalysis(intelligence, topics);
  }
}

export function getRecommendedLearningPath(
  intelligence: IntelligenceState,
  topics: Topic[],
): RecommendedPath {
  const gapTopicIds = Object.entries(intelligence.masteryState.topicMastery)
    .filter(([_, params]) => params.pKnown < 0.5)
    .map(([id]) => id);

  if (gapTopicIds.length === 0) {
    return { steps: [], totalSteps: 0, estimatedDuration: '0 min', rootCauseCount: 0, gapCount: 0 };
  }

  return { steps: [], totalSteps: 0, estimatedDuration: '0 min', rootCauseCount: 0, gapCount: gapTopicIds.length };
}

export async function fetchRecommendedLearningPath(
  intelligence: IntelligenceState,
  topics: Topic[],
  subjectId?: string,
): Promise<RecommendedPath> {
  const gapTopicIds = Object.entries(intelligence.masteryState.topicMastery)
    .filter(([_, params]) => params.pKnown < 0.5)
    .map(([id]) => id);

  if (gapTopicIds.length === 0 || !API_BASE) {
    return getRecommendedLearningPath(intelligence, topics);
  }

  try {
    const data = await fetchJson('/api/infer/recommend-path', {
      studentId: intelligence.masteryState.studentId,
      gaps: gapTopicIds,
      subjectId,
    });

    return {
      steps: (data.steps || []).map((s: any) => ({
        topicId: s.topicId || s.concept,
        name: s.topicName || s.conceptName,
        reason: s.recommendation || `Mastery: ${Math.round((s.mastery ?? 0) * 100)}%`,
      })),
      totalSteps: data.totalSteps ?? 0,
      estimatedDuration: data.estimatedDuration ?? '0 min',
      rootCauseCount: data.rootCauseCount ?? 0,
      gapCount: data.gapCount ?? gapTopicIds.length,
    };
  } catch {
    return getRecommendedLearningPath(intelligence, topics);
  }
}

export function getNextSocraticExchange(
  state: IntelligenceState,
  topics: Topic[]
): SocraticExchange | null {
  const masteryMap = new Map(
    Object.entries(state.masteryState.topicMastery).map(([id, params]) => [id, params.pKnown])
  );
  
  const masteryGaps = Object.entries(state.masteryState.topicMastery)
    .map(([topicId, params]) => ({ topicId, mastery: params.pKnown }))
    .sort((a, b) => a.mastery - b.mastery);
  
  const selection = selectNextDialogueTopic(state.gapSignals, topics, masteryGaps);
  
  if (!selection) return null;
  
  return generateSocraticDialogue(
    state.gapSignals[0],
    selection.topic,
    state.socraticHistory
  );
}

export function processSocraticResponse(
  state: IntelligenceState,
  exchangeId: string,
  studentResponse: string
): {
  updatedState: IntelligenceState;
  analysis: ReturnType<typeof analyzeStudentResponse>;
  shouldEscalate: boolean;
  updatedMastery: StudentMasteryState;
} {
  const exchange = state.socraticHistory.find(e => e.id === exchangeId);
  
  if (!exchange) {
    return {
      updatedState: state,
      analysis: {
        misconceptionExposed: false,
        understandingImproved: false,
        needsEscalation: false,
        insight: "Exchange not found"
      },
      shouldEscalate: false,
      updatedMastery: state.masteryState
    };
  }
  
  const analysis = analyzeStudentResponse(studentResponse, {
    question: exchange.socraticQuestion,
    type: "probing",
    purpose: "Assess understanding"
  });
  
  let updatedState = {
    ...state,
    socraticHistory: state.socraticHistory.map(e =>
      e.id === exchangeId
        ? { ...e, studentResponse, misconceptionExposed: analysis.misconceptionExposed }
        : e
    )
  };
  
  let updatedMastery = { ...state.masteryState };
  
  if (analysis.understandingImproved) {
    const topicMastery = updatedMastery.topicMastery[exchange.topicId];
    if (topicMastery) {
      const update = updateBKTMasteryFromGapSignal(
        topicMastery,
        "low",
        0.6
      );
      updatedMastery.topicMastery[exchange.topicId] = update.updatedParams;
    }
  }
  
  const escalation = shouldEscalateToDirectInstruction(updatedState.socraticHistory);
  
  return {
    updatedState,
    analysis,
    shouldEscalate: escalation.shouldEscalate || analysis.needsEscalation,
    updatedMastery
  };
}

export function createTargetedAssessment(
  state: IntelligenceState,
  targetTopicId: string,
  topics: Topic[],
  questionBank: QuizQuestion[]
): {
  assessment: MicroAssessment;
  updatedState: IntelligenceState;
} {
  const topic = topics.find(t => t.id === targetTopicId);
  if (!topic) {
    throw new Error(`Topic ${targetTopicId} not found`);
  }
  
  let calibratedQuestions = new Map(state.calibratedQuestions);
  
  for (const question of questionBank) {
    if (!calibratedQuestions.has(question.id)) {
      const calibrated = calibrateQuestion(question, targetTopicId);
      calibratedQuestions.set(question.id, calibrated);
    }
  }
  
  const existingAssessment = state.assessments.get(targetTopicId);
  if (existingAssessment) {
    return {
      assessment: existingAssessment,
      updatedState: { ...state, calibratedQuestions }
    };
  }
  
  const assessment = createMicroAssessment(topic, Array.from(calibratedQuestions.values()));
  
  const updatedAssessments = new Map(state.assessments);
  updatedAssessments.set(targetTopicId, assessment);
  
  return {
    assessment,
    updatedState: { ...state, assessments: updatedAssessments, calibratedQuestions }
  };
}

export function buildUnderstandingProfile(
  state: IntelligenceState,
  topics: Topic[]
): StudentUnderstandingProfile {
  const masteryMap = getMasteryVisualization(state, topics);
  const analysis = getRootCauseAnalysis(state, topics);
  
  return {
    studentId: state.masteryState.studentId,
    studentName: "Student",
    subjectId: topics[0]?.subjectId || "",
    mastery: masteryMap.map(m => ({
      topicId: m.topicId,
      topicName: m.topicName,
      masteryPercent: m.percent,
      evidence: `BKT mastery at ${m.percent}%`
    })),
    gapSignals: state.gapSignals,
    conceptMastery: [],
    recommendedFocus: analysis.rootCauses.rootCauses.slice(0, 5).map(rc => ({
      conceptId: rc.topicId,
      conceptName: rc.topicName,
      topicId: rc.topicId,
      topicName: rc.topicName,
      reason: analysis.rootCauses.explanation,
      confidence: rc.impactScore
    })),
    inferredFrom: {
      quizAttempts: Object.values(state.masteryState.topicMastery)
        .reduce((sum, p) => sum + p.attempts, 0),
      chatQuestions: state.socraticHistory.length
    }
  };
}
